// Verificação em duas etapas (MFA): tela de código e configurações.
// Extraído de App.js sem alteração de comportamento.
import { useState, useEffect } from "react";
import { sb } from "../lib/supabase";
import { useTheme } from "../lib/theme";
import { NavIcon } from "./icons";
import { appConfirm, appAlert } from "../lib/dialog";
import { LEGAL } from "../lib/constants";
import { authPalette } from "./authUi";

// ── ACCOUNT SETTINGS ─────────────────────────────────────────────────────────
// ── VERIFICAÇÃO EM DUAS ETAPAS (aplicativo autenticador, TOTP) ─────────────────
function mfaQrSrc(qr) {                 // o Supabase devolve o SVG "cru"; codifica para funcionar em todo navegador
  const p = "data:image/svg+xml;utf-8,";
  return qr && qr.startsWith(p) ? "data:image/svg+xml;charset=utf-8," + encodeURIComponent(qr.slice(p.length)) : qr;
}

function MfaCodeInput({ value, onChange, onEnter, t, autoFocus = true }) {
  return <input value={value} autoFocus={autoFocus} inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="000000" aria-label="Código de 6 dígitos"
    onChange={e => onChange(e.target.value.replace(/\D/g, "").slice(0, 6))} onKeyDown={e => { if (e.key === "Enter") onEnter && onEnter(); }}
    style={{ width: "100%", boxSizing: "border-box", textAlign: "center", letterSpacing: "0.5em", fontSize: "1.5rem", fontWeight: 800, padding: "0.7rem 0.5rem 0.7rem 1rem",
      background: t.inputBg || t.bgInner, border: `1px solid ${t.border || "rgba(255,255,255,0.12)"}`, borderRadius: 12, color: t.text, outline: "none", fontFamily: t.fontMono || "ui-monospace,monospace" }} />;
}

export function MfaChallengeScreen({ checking, onDone, onLogout }) {
  const P = authPalette();
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit() {
    if (code.length !== 6 || busy) return;
    setBusy(true); setErr("");
    try {
      const { data: f, error: e1 } = await sb.auth.mfa.listFactors();
      const factor = !e1 && f && (f.totp || []).find(x => x.status === "verified");
      if (!factor) { setErr("Não encontramos o autenticador desta conta. Saia e entre de novo."); setBusy(false); return; }
      const { error } = await sb.auth.mfa.challengeAndVerify({ factorId: factor.id, code });
      if (error) { setErr("Código inválido ou expirado. Confira o horário do celular e tente o código atual."); setCode(""); setBusy(false); return; }
      onDone();
    } catch { setErr("Não foi possível verificar agora. Tente de novo."); setBusy(false); }
  }
  useEffect(() => { if (code.length === 6) submit(); /* envia ao completar */ // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);
  const inputT = { inputBg: P.inputBg, border: P.tickOff, text: P.text, fontMono: "'JetBrains Mono',ui-monospace,monospace" };
  return (
    <div style={{ minHeight: "100dvh", background: P.pageBg, display: "flex", alignItems: "center", justifyContent: "center", padding: "1.2rem", fontFamily: P.fontBody, color: P.text }}>
      <div style={{ width: "100%", maxWidth: 400, background: P.cardBg, borderRadius: 18, padding: "2rem 1.6rem", boxShadow: P.cardShadow, border: `1px solid rgba(${P.rgb},0.18)` }}>
        {checking ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "2rem" }}><div style={{ width: 36, height: 36, border: `3px solid ${P.ac}`, borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} /><style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style></div>
        ) : <>
          <div style={{ width: 52, height: 52, borderRadius: 14, background: `rgba(${P.rgb},0.12)`, border: `1px solid rgba(${P.rgb},0.35)`, color: P.ac, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1rem" }}>
            <NavIcon id="admin" size={26} />
          </div>
          <h1 style={{ margin: "0 0 0.4rem", textAlign: "center", fontSize: "1.25rem", fontWeight: 800, fontFamily: P.fontBody }}>Verificação em duas etapas</h1>
          <p style={{ margin: "0 0 1.3rem", textAlign: "center", fontSize: "0.88rem", lineHeight: 1.55, color: P.dim }}>Abra seu aplicativo autenticador e digite o código de 6 dígitos do G17.</p>
          <MfaCodeInput value={code} onChange={v => { setCode(v); setErr(""); }} onEnter={submit} t={inputT} />
          {err && <p role="alert" style={{ margin: "0.7rem 0 0", fontSize: "0.8rem", color: P.errText, textAlign: "center", lineHeight: 1.45 }}>{err}</p>}
          <button onClick={submit} disabled={code.length !== 6 || busy}
            style={{ width: "100%", marginTop: "1rem", minHeight: 46, border: "none", borderRadius: 12, background: P.btnBg, color: P.onAc, fontWeight: 800, fontSize: "0.95rem", cursor: code.length === 6 && !busy ? "pointer" : "default", opacity: code.length === 6 && !busy ? 1 : 0.55, fontFamily: "inherit" }}>
            {busy ? "Verificando…" : "Verificar"}
          </button>
          <button onClick={onLogout} style={{ width: "100%", marginTop: "0.6rem", minHeight: 40, background: "transparent", border: "none", color: P.dim, fontSize: "0.84rem", cursor: "pointer", fontFamily: "inherit" }}>Sair e entrar com outra conta</button>
          <p style={{ margin: "1rem 0 0", fontSize: "0.72rem", color: P.faint, textAlign: "center", lineHeight: 1.5 }}>Perdeu o celular? Escreva para <a href={`mailto:${LEGAL.email}`} style={{ color: P.dim }}>{LEGAL.email}</a> a partir do e-mail da conta.</p>
        </>}
      </div>
    </div>
  );
}

export function TwoFactorSettings() {
  const t = useTheme();
  const [factor, setFactor] = useState(undefined);   // undefined = carregando, null = desativada
  const [enroll, setEnroll] = useState(null);        // { id, qr, secret }
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  async function load() {
    try {
      const { data, error } = await sb.auth.mfa.listFactors();
      setFactor(!error && data ? ((data.totp || []).find(x => x.status === "verified") || null) : null);
    } catch { setFactor(null); }
  }
  useEffect(() => { load(); }, []);

  async function start() {
    setBusy(true); setErr(""); setCode("");
    try {
      const { data: all } = await sb.auth.mfa.listFactors();       // limpa tentativas antigas não concluídas
      for (const f of ((all && all.all) || []).filter(x => x.status !== "verified")) { try { await sb.auth.mfa.unenroll({ factorId: f.id }); } catch {} }
      const { data, error } = await sb.auth.mfa.enroll({ factorType: "totp", friendlyName: "G17 " + new Date().toLocaleDateString("pt-BR"), issuer: "G17" });
      if (error) throw error;
      setEnroll({ id: data.id, qr: mfaQrSrc(data.totp.qr_code), secret: data.totp.secret });
    } catch (e) { setErr("Não foi possível iniciar agora. " + (e?.message || "")); }
    setBusy(false);
  }
  async function cancel() {
    if (enroll) { try { await sb.auth.mfa.unenroll({ factorId: enroll.id }); } catch {} }
    setEnroll(null); setCode(""); setErr("");
  }
  async function confirm() {
    if (code.length !== 6 || busy) return;
    setBusy(true); setErr("");
    const { error } = await sb.auth.mfa.challengeAndVerify({ factorId: enroll.id, code });
    setBusy(false);
    if (error) { setErr("Código inválido. Confira se o horário do celular está automático e digite o código atual."); setCode(""); return; }
    setEnroll(null); setCode("");
    await load();
    appAlert("A partir do próximo login, o G17 vai pedir o código do aplicativo depois da senha.", { title: "Verificação em duas etapas ativada", icon: "checkcirc" });
  }
  async function disable() {
    if (!factor) return;
    if (!(await appConfirm("Sua conta voltará a pedir só a senha para entrar.", { title: "Desativar a verificação em duas etapas?", confirmText: "Desativar", danger: true }))) return;
    const { error } = await sb.auth.mfa.unenroll({ factorId: factor.id });
    if (error) { appAlert("Saia e entre de novo com o código do aplicativo, depois tente desativar.", { title: "Não foi possível desativar", icon: "alerta" }); return; }
    try { await sb.auth.refreshSession(); } catch {}
    load();
  }
  function copySecret() {
    try { navigator.clipboard.writeText(enroll.secret); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch {}
  }

  const box = { background: t.bgInner, border: `1px solid ${t.border}`, borderRadius: 10, padding: "0.8rem 0.9rem" };
  return (
    <div style={{ ...box, marginTop: "0.2rem" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.7rem" }}>
        <span style={{ color: factor ? "#4ade80" : t.textDim, display: "flex" }}><NavIcon id="admin" size={18} /></span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: "0.85rem", fontWeight: 700, color: t.text }}>Verificação em duas etapas</p>
          <p style={{ margin: "0.1rem 0 0", fontSize: "0.72rem", color: factor ? "#4ade80" : t.textDim }}>
            {factor === undefined ? "Verificando…" : factor ? `Ativada desde ${new Date(factor.created_at).toLocaleDateString("pt-BR")}` : "Desativada. Use um aplicativo autenticador para proteger a conta."}
          </p>
        </div>
        {factor !== undefined && !enroll && (factor
          ? <button onClick={disable} style={{ background: "transparent", border: "1px solid #f8717155", color: "#f87171", borderRadius: 8, padding: "0.4rem 0.75rem", fontSize: "0.76rem", fontWeight: 600, cursor: "pointer", flexShrink: 0 }}>Desativar</button>
          : <button onClick={start} disabled={busy} style={{ background: t.ac, border: "none", color: t.onAc || "#fff", borderRadius: 8, padding: "0.45rem 0.85rem", fontSize: "0.76rem", fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>{busy ? "Aguarde…" : "Ativar"}</button>)}
      </div>
      {err && !enroll && <p style={{ margin: "0.6rem 0 0", fontSize: "0.76rem", color: "#f87171" }}>{err}</p>}

      {enroll && (
        <div style={{ marginTop: "0.9rem", borderTop: `1px solid ${t.borderIn}`, paddingTop: "0.9rem" }}>
          <p style={{ margin: "0 0 0.6rem", fontSize: "0.8rem", color: t.textMid, lineHeight: 1.55 }}>
            <strong style={{ color: t.text }}>1.</strong> Instale um aplicativo autenticador (Google Authenticator, Microsoft Authenticator, Authy ou 1Password) e escaneie o código abaixo.
          </p>
          <div style={{ display: "flex", gap: "1rem", alignItems: "center", flexWrap: "wrap", marginBottom: "0.8rem" }}>
            <div style={{ background: "#fff", borderRadius: 12, padding: 10, lineHeight: 0 }}>
              <img src={enroll.qr} alt="QR code para o aplicativo autenticador" width={164} height={164} style={{ display: "block" }} />
            </div>
            <div style={{ flex: 1, minWidth: 180 }}>
              <p style={{ margin: "0 0 0.3rem", fontSize: "0.72rem", color: t.textDim }}>Não consegue escanear? Digite esta chave no aplicativo:</p>
              <code className="g17-selectable" style={{ display: "block", fontSize: "0.78rem", color: t.text, background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 8, padding: "0.45rem 0.55rem", wordBreak: "break-all", letterSpacing: "0.05em" }}>{enroll.secret}</code>
              <button onClick={copySecret} style={{ marginTop: "0.4rem", background: "transparent", border: "none", color: t.ac, fontSize: "0.74rem", cursor: "pointer", padding: 0 }}>{copied ? "Copiada ✓" : "Copiar chave"}</button>
            </div>
          </div>
          <p style={{ margin: "0 0 0.5rem", fontSize: "0.8rem", color: t.textMid }}><strong style={{ color: t.text }}>2.</strong> Digite o código de 6 dígitos que aparece no aplicativo:</p>
          <MfaCodeInput value={code} onChange={v => { setCode(v); setErr(""); }} onEnter={confirm} t={t} />
          {err && <p role="alert" style={{ margin: "0.5rem 0 0", fontSize: "0.76rem", color: "#f87171", lineHeight: 1.45 }}>{err}</p>}
          <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end", marginTop: "0.8rem" }}>
            <button onClick={cancel} style={{ background: "transparent", border: `1px solid ${t.border}`, color: t.textMid, borderRadius: 8, padding: "0.5rem 0.9rem", fontSize: "0.8rem", cursor: "pointer" }}>Cancelar</button>
            <button onClick={confirm} disabled={code.length !== 6 || busy} style={{ background: t.ac, border: "none", color: t.onAc || "#fff", borderRadius: 8, padding: "0.5rem 1rem", fontSize: "0.8rem", fontWeight: 700, cursor: code.length === 6 ? "pointer" : "default", opacity: code.length === 6 && !busy ? 1 : 0.55 }}>{busy ? "Verificando…" : "Ativar"}</button>
          </div>
          <p style={{ margin: "0.7rem 0 0", fontSize: "0.7rem", color: t.textFaint, lineHeight: 1.5 }}>Guarde bem o aplicativo: sem ele, só dá para entrar pedindo ajuda em {LEGAL.email}.</p>
        </div>
      )}
    </div>
  );
}
